import { Fragment, useCallback } from 'react';
import { useRouter } from 'next/router';
import { twMerge } from 'tailwind-merge';
import cn from 'classnames';
import { IBreadcrumb, IBreadCrumbItem } from 'src/interface';
import { UrlUtils } from 'src/utils';
import { Element, Icon, Image, RouterLink } from 'src/components';
import Level from 'src/components/grid/level';
import FollowButton from 'src/modules/shared/components/buttons/FollowButton';

interface IProps {
    breadcrumb: IBreadcrumb;
    className?: string;
}

export const BreadCrumb = ({ breadcrumb, className = '' }: IProps) => {
    const { left = [], right = [] } = breadcrumb;
    const router = useRouter();
    const renderItems = useCallback(
        (items: IBreadCrumbItem[] = [], isLeft: boolean = false) => {
            return items.map((item: IBreadCrumbItem, index: number) => {
                let isH1 = false;
                if (isLeft) {
                    if (
                        index === items.length - 1 &&
                        !UrlUtils.isContentDetailUrl(router.asPath)
                    ) {
                        isH1 = true;
                    }
                }
                return (
                    <Fragment key={`breadcrumb.${index}`}>
                        {item.html ? (
                            <Element
                                dangerouslySetInnerHTML={{ __html: item.html }}
                            />
                        ) : (
                            <>
                                <RouterLink
                                    to={item.url}
                                    className={twMerge(
                                        'item',
                                        'relative',
                                        'shrink-0',
                                        'text-[1.5rem] font-semibold uppercase',
                                        cn({
                                            ['normal-case px-[5px]']: !isLeft,
                                            [cn(
                                                'flex items-center',
                                                'text-[#6c757d]',
                                                'pl-[28px]',

                                                'first:pl-0',
                                                'first:text-[var(--primary)]'
                                            )]: isLeft,
                                        })
                                    )}
                                    style={item.styles || {}}
                                    title={item.name}
                                >
                                    {item.logo ? (
                                        <>
                                            <Element renderAs="h1">
                                                <Image
                                                    className="h-[20px] [&_img]:w-auto"
                                                    src={item.logo}
                                                    alt={item.name}
                                                ></Image>
                                            </Element>
                                            <Element
                                                renderAs="span"
                                                className="ml-[5px]"
                                            >
                                                {item.name}
                                            </Element>
                                        </>
                                    ) : (
                                        <>
                                            {index !== 0 && isLeft && (
                                                <Icon
                                                    className={cn(
                                                        'absolute left-[12px] top-2/4',
                                                        '-translate-y-2/4',
                                                        'text-[1rem] text-[#6c757d]'
                                                    )}
                                                    icon="chevron-right-bold"
                                                />
                                            )}
                                            {isH1 ? (
                                                <Element renderAs="h1">
                                                    {item.name}
                                                </Element>
                                            ) : (
                                                <Element renderAs="span">
                                                    {item.name}
                                                </Element>
                                            )}
                                        </>
                                    )}
                                </RouterLink>
                                {item.showFollowBtn && (
                                    <FollowButton
                                        objectId={item.id}
                                        objectType={`${item.objectType}`}
                                        className={cn(
                                            'ml-[8px]',
                                            'text-[1.2rem]',
                                            'text-[var(--primary)]',
                                            'relative bottom-[1px]'
                                        )}
                                    />
                                )}
                            </>
                        )}
                    </Fragment>
                );
            });
        },
        [router.asPath]
    );

    return (
        <Level
            className={twMerge(
                'breadcrumb',
                'text-[#212529] w-full mb-[11px]',
                'overflow-hidden',
                'font-semibold',
                '[&::-webkit-scrollbar]:hidden',
                className
            )}
        >
            <Level.Side align="left">{renderItems(left, true)}</Level.Side>
            <Level.Side align="right">{renderItems(right)}</Level.Side>
        </Level>
    );
};
